import { AnimatePresence, motion } from 'framer-motion';
import { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Box,
  type BoxProps,
  Flex,
  Text,
  IconButton,
  useBreakpointValue,
  Tooltip,
  Button,
  lighten,
} from '@vimeo/bokeh';
import { SearchMagnifier, Link, ArrowLeft, SettingsGear } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';
import useOnClickOutside from '@vimeo/ui/hooks/use-outside-click/useOnClickOutside';

import { withResponsiveValue } from '../../hocs/withResponsiveValue';
import { getIsActiveState } from '../../utils/helper';
import { getBorderStyles } from '../../utils/style';
import { ResponsiveValue } from '../../utils/types';
import { HoverState } from '../HoverState';
import { SearchBar } from '../SearchBar';
import { type SearchResultsProps } from '../SearchResults';

export type ShowcaseNavigationProps = BoxProps &
  Partial<SearchResultsProps> & {
    allowShare?: boolean;
    customLogo?: string;
    customLogoSrcSet?: string;
    isNewCustomizeControls?: boolean;
    isShowcaseEmpty?: boolean;
    isShowcasePreview?: boolean;
    isSearchVisible?: boolean;
    onShareClick?: () => void;
    onBackClick?: () => void;
    onLogoClick?: () => void;
    onSearch?: (text: string) => void;
    onResetSearch?: () => void;
    onResultClick?: () => void;
    textColor?: string;
    topTitle?: string;
    showBackButton?: boolean;
    showManageButton?: boolean;
    onManageClick?: () => void;
    isMobilePreview?: boolean;
    showcaseId?: number;
  };

const ShowcaseNavigationComponent = ({
  allowShare = false,
  customLogo,
  customLogoSrcSet,
  isNewCustomizeControls,
  isShowcaseEmpty,
  isShowcasePreview,
  isSearchVisible = true,
  showBackButton,
  showManageButton,
  onBackClick,
  onLogoClick,
  onLoadMoreSearchResults,
  onResetSearch,
  onResultClick,
  onSearch,
  onShareClick,
  onManageClick,
  canLoadMore = false,
  resultsPending = false,
  resultsList = [],
  searchedTerm = '',
  searchError = false,
  textColor = 'white',
  topTitle = '',
  isMobilePreview,
  getResponsiveValue,
  showcaseId,
  ...rest
}: ShowcaseNavigationProps & ResponsiveValue) => {
  const isClipView = showBackButton;
  const searchBarRef = useRef<HTMLDivElement>(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const isSmallScreen = useBreakpointValue({ base: true, md: isMobilePreview ? true : false });
  const onlyShowSearchBar = isSmallScreen && showSearchBar;
  const flexGrow = useBreakpointValue({ base: undefined, md: isMobilePreview ? undefined : 1 });
  const showDefaultButtonColor = isClipView || textColor === 'text-primary';
  // We always need to render it in md+ screens to keep the title at absolute center. In small screens, we can hide it so the title can be left aligned
  const renderLeftComponents = useBreakpointValue({
    base: customLogo || showBackButton,
    md: isMobilePreview ? customLogo || showBackButton : true,
  });

  const topTitleAs = isShowcasePreview || isClipView ? undefined : 'h1';
  const history = useHistory();
  const isActiveState = getIsActiveState(history?.location?.pathname, 'navigation');

  const expandSearch = () => {
    setShowSearchBar(true);
  };

  useOnClickOutside(
    [searchBarRef],
    () => {
      if (showSearchBar) {
        setShowSearchBar(false);
      }
    },
    null,
    [showSearchBar],
  );

  const searchBarProps = {
    canLoadMore,
    resultsPending,
    resultsList,
    searchedTerm,
    searchError,
    textColor,
    onLoadMoreSearchResults,
    onResetSearch,
    onResultClick,
    onSearch,
    onDismissSearch: () => setShowSearchBar(false),
  };

  if (onlyShowSearchBar) {
    return (
      <Flex
        ref={searchBarRef}
        width={getResponsiveValue({ base: '100%', md: '200px' })}
        justifyContent="space-between"
        alignItems="center"
        p="md"
        px="lg"
        {...rest}
      >
        <SearchBar {...searchBarProps} />
      </Flex>
    );
  }

  return (
    <Flex
      gap="sm"
      justifyContent="space-between"
      alignItems="center"
      p="md"
      px="lg"
      {...(isNewCustomizeControls
        ? getBorderStyles(isShowcasePreview, isActiveState, 'navigation')
        : {})}
      {...rest}
    >
      {isShowcasePreview && (
        <HoverState
          isNewCustomizeControls={isNewCustomizeControls}
          showcaseId={showcaseId}
          type="navigation"
        />
      )}
      {renderLeftComponents && (
        <Flex
          pr="sm"
          alignItems="center"
          flexBasis={1}
          flexGrow={flexGrow}
          gap={getResponsiveValue({ base: 'xs', md: 'sm' })}
        >
          <AnimatePresence>
            {showBackButton ? (
              <Tooltip label={translate('Back')}>
                <IconButton
                  as={motion.button}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  color={textColor}
                  aria-label={translate('Back')}
                  size="md"
                  variant="tertiary"
                  icon={<ArrowLeft />}
                  onClick={onBackClick}
                />
              </Tooltip>
            ) : null}
            {customLogo ? (
              <Box
                as={motion.img}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                position="relative"
                overflow="hidden"
                height="32px"
                width="auto"
                src={customLogo}
                srcSet={customLogoSrcSet}
                alt="Showcase logo"
                onClick={onLogoClick}
              />
            ) : null}
          </AnimatePresence>
        </Flex>
      )}
      {!showSearchBar && ( // Hide title when search bar is expanded
        <Flex noOfLines={1} justifyContent="center" alignItems="center">
          <Text
            as={topTitleAs}
            whiteSpace="nowrap"
            textAlign={'center'}
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            variant="heading-sm"
            color={textColor}
          >
            {topTitle}
          </Text>
        </Flex>
      )}
      <Flex
        flexGrow={flexGrow}
        justifyContent="flex-end"
        ref={searchBarRef}
        alignItems="center"
        gap={getResponsiveValue({ base: 'xs', md: 'sm' })}
        flexBasis={1}
        order={2}
      >
        {showSearchBar ? (
          <Box width={getResponsiveValue({ base: '100%', sm: '480px' })}>
            <SearchBar {...searchBarProps} />
          </Box>
        ) : !isShowcaseEmpty && isSearchVisible ? (
          <Tooltip label={translate('Search showcase')}>
            <IconButton
              color={textColor}
              aria-label={translate('Search showcase')}
              size="md"
              variant="blur"
              icon={<SearchMagnifier />}
              onClick={expandSearch}
              _hover={{
                bgColor: showDefaultButtonColor
                  ? 'fill-component-hover'
                  : lighten('#3D4751A3', 0.1),
              }}
              bgColor={showDefaultButtonColor ? undefined : '#3D4751A3'}
            />
          </Tooltip>
        ) : null}

        {allowShare && (
          <Tooltip label={translate('Copy link')}>
            <IconButton
              color={textColor}
              onClick={onShareClick}
              aria-label={translate('Copy link')}
              size="md"
              variant="blur"
              icon={<Link />}
              _hover={{
                bgColor: showDefaultButtonColor
                  ? 'fill-component-hover'
                  : lighten('#3D4751A3', 0.1),
              }}
              bgColor={showDefaultButtonColor ? undefined : '#3D4751A3'}
            />
          </Tooltip>
        )}

        {showManageButton && (
          <>
            {isSmallScreen ? (
              <Tooltip label={translate('Manage')}>
                <IconButton
                  onClick={onManageClick}
                  aria-label={translate('Manage')}
                  size="md"
                  variant="blur"
                  color={textColor}
                  icon={<SettingsGear />}
                  _hover={{
                    bgColor: showDefaultButtonColor
                      ? 'fill-component-hover'
                      : lighten('#3D4751A3', 0.1),
                  }}
                  bgColor={showDefaultButtonColor ? undefined : '#3D4751A3'}
                />
              </Tooltip>
            ) : (
              <Button
                onClick={onManageClick}
                aria-label={translate('Manage')}
                size="md"
                variant="blur"
                color={textColor}
                bgColor={showDefaultButtonColor ? undefined : '#3D4751A3'}
                _hover={{
                  bgColor: showDefaultButtonColor
                    ? 'fill-component-hover'
                    : lighten('#3D4751A3', 0.1),
                }}
              >
                {translate('Manage')}
              </Button>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

export const ShowcaseNavigation = withResponsiveValue(ShowcaseNavigationComponent);
